/**
 * 修复画笔控制器（补齐 registry.ts 早先登记的占位工具）。
 *
 * 复用画笔同款的采样/缓冲管线（StrokeSampler + StrokeBuffer）积累笔画覆盖度，
 * 收笔时把覆盖区域交给 app/heal-fill.ts 跑一次内容识别填充。没有直接复用
 * BrushToolController——那个类的提交路径是同步的，修复画笔的提交要跑
 * wasm PatchMatch（异步），硬套只会把两种语义搅在一起。
 */
import type { DocumentPoint } from '../viewport';
import { StrokeSampler, type InputPoint, type SamplerSettings } from '../../engine/brush/sampler';
import { StrokeBuffer } from '../../engine/brush/buffer';
import { healStrokeDocument } from '../../app/heal-fill';
import type { AppStore } from '../../app/store';
import type { ToolManager } from './registry';

export interface HealToolSample extends DocumentPoint {
  readonly pressure: number | null;
  readonly timeMs: number;
}

export interface HealToolDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly requestRender: () => void;
}

export class HealToolController {
  readonly #deps: HealToolDeps;
  #sampler: StrokeSampler | null = null;
  #buffer: StrokeBuffer | null = null;
  #preview: HTMLCanvasElement | null = null;
  #busy = false;

  constructor(deps: HealToolDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#sampler !== null;
  }

  /** 处理指针。当前工具不是修复画笔时返回 false，让调用方交给别的工具。 */
  handlePointer(
    phase: 'down' | 'move' | 'up' | 'double',
    samples: readonly HealToolSample[],
  ): boolean {
    const { tools, store } = this.#deps;
    if (tools.active.id !== 'heal') return false;
    const document = store.document;
    if (document === null) return true;

    if (phase === 'down') {
      if (this.#busy) return true; // 上一笔的 PatchMatch 还没跑完，不叠新笔
      this.#begin(document.size);
    }
    if (this.#sampler === null) return true;

    if (phase === 'down' || phase === 'move') {
      for (const sample of samples) this.#feed(sample);
      this.#deps.requestRender();
    } else if (phase === 'up') {
      this.#commit();
    }
    return true;
  }

  /** 文档空间的实时预览：半透明白示意"这块会被内容识别替换"。 */
  drawPreview(ctx: CanvasRenderingContext2D): void {
    if (this.#preview === null) return;
    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.drawImage(this.#preview, 0, 0);
    ctx.restore();
  }

  #begin(size: { width: number; height: number }): void {
    const tools = this.#deps.tools;
    const diameter = tools.getOption('heal', 'size') as number;
    const hardness = (tools.getOption('heal', 'hardness') as number) / 100;
    const settings: SamplerSettings = {
      size: diameter,
      flow: 1,
      spacing: 0.25,
      smoothing: 0.5,
      pressureSize: true,
      pressureFlow: true,
    };
    this.#sampler = new StrokeSampler(settings);
    this.#buffer = new StrokeBuffer(size.width, size.height, hardness);

    if (typeof document !== 'undefined') {
      this.#preview ??= document.createElement('canvas');
      this.#preview.width = size.width;
      this.#preview.height = size.height;
      this.#preview.getContext('2d')?.clearRect(0, 0, size.width, size.height);
    }
  }

  #feed(sample: HealToolSample): void {
    const sampler = this.#sampler;
    const buffer = this.#buffer;
    if (sampler === null || buffer === null) return;
    const input: InputPoint = {
      x: sample.x,
      y: sample.y,
      pressure: sample.pressure,
      timeMs: sample.timeMs,
    };
    const stamps = sampler.add(input);
    if (stamps.length === 0) return;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const stamp of stamps) {
      buffer.stamp(stamp);
      minX = Math.min(minX, stamp.x - stamp.size / 2);
      minY = Math.min(minY, stamp.y - stamp.size / 2);
      maxX = Math.max(maxX, stamp.x + stamp.size / 2);
      maxY = Math.max(maxY, stamp.y + stamp.size / 2);
    }
    this.#updatePreview(minX, minY, maxX, maxY);
  }

  #updatePreview(minX: number, minY: number, maxX: number, maxY: number): void {
    const buffer = this.#buffer;
    const preview = this.#preview;
    if (buffer === null || preview === null) return;
    const ctx = preview.getContext('2d');
    if (ctx === null) return;
    const x0 = Math.max(0, Math.floor(minX));
    const y0 = Math.max(0, Math.floor(minY));
    const x1 = Math.min(preview.width, Math.ceil(maxX));
    const y1 = Math.min(preview.height, Math.ceil(maxY));
    if (x0 >= x1 || y0 >= y1) return;

    const region = ctx.createImageData(x1 - x0, y1 - y0);
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) {
        const alpha = buffer.alphaAt(x, y);
        if (alpha <= 0) continue;
        const at = ((y - y0) * (x1 - x0) + (x - x0)) * 4;
        region.data[at] = 255;
        region.data[at + 1] = 255;
        region.data[at + 2] = 255;
        region.data[at + 3] = Math.round(alpha * 255);
      }
    }
    ctx.putImageData(region, x0, y0);
  }

  #commit(): void {
    const sampler = this.#sampler;
    const buffer = this.#buffer;
    const store = this.#deps.store;
    this.#sampler = null;
    this.#buffer = null;
    if (sampler === null || buffer === null) return;

    for (const stamp of sampler.finish()) buffer.stamp(stamp);

    // 选区裁剪：与画笔同一纪律——选区外画不上（这里是"修不到"）
    const selection = store.selection;
    if (selection !== null) buffer.clipToMask(selection);

    this.#busy = true;
    void healStrokeDocument(store, buffer)
      .catch((error: unknown) => {
        store.setHint(`污点修复画笔失败：${error instanceof Error ? error.message : String(error)}`);
      })
      .finally(() => {
        this.#busy = false;
        this.#preview?.getContext('2d')?.clearRect(0, 0, this.#preview.width, this.#preview.height);
        this.#deps.requestRender();
      });
  }
}
